import type { Timestamp } from 'firebase/firestore';

// ============================================
// PAGE TYPES
// ============================================

/**
 * Available page types for page-specific effects.
 */
export type PageType = 'study' | 'shmooze' | 'library' | 'prayers' | 'journey';

/**
 * All page types as an array for iteration.
 */
export const PAGE_TYPES: PageType[] = ['study', 'shmooze', 'library', 'prayers', 'journey'];

/**
 * Page display information.
 */
export const PAGE_INFO: Record<PageType, { label: string; description: string }> = {
  study: { label: 'Study', description: 'Torah portion and weekly study' },
  shmooze: { label: 'Shmooze', description: 'Community chat and messages' },
  library: { label: 'Library', description: 'Class materials and resources' },
  prayers: { label: 'Prayers', description: 'Community prayer lists' },
  journey: { label: 'Journey', description: 'Personal profile and progress' },
};

// ============================================
// GRADIENT CONFIGURATION
// ============================================

/**
 * Gradient position presets.
 */
export type GradientPosition = 'top-center' | 'center' | 'corner-right' | 'bottom' | 'corner-left';

/**
 * Gradient type options.
 */
export type GradientType = 'radial' | 'linear';

/**
 * Configuration for gradient layer.
 */
export interface GradientConfig {
  enabled: boolean;
  type: GradientType;
  position: GradientPosition;
  colors: [string, string]; // Primary and secondary colors
  opacity: number; // 0-1
  size: number; // Percentage of canvas (0.5-2.0)
}

export const GRADIENT_POSITION_OPTIONS: { value: GradientPosition; label: string }[] = [
  { value: 'top-center', label: 'Top Center' },
  { value: 'center', label: 'Centered' },
  { value: 'corner-right', label: 'Top Right Corner' },
  { value: 'corner-left', label: 'Top Left Corner' },
  { value: 'bottom', label: 'Bottom' },
];

export const GRADIENT_TYPE_OPTIONS: { value: GradientType; label: string }[] = [
  { value: 'radial', label: 'Radial (Circular)' },
  { value: 'linear', label: 'Linear (Directional)' },
];

// ============================================
// PARTICLE CONFIGURATION
// ============================================

/**
 * Particle shape options - Jewish-themed sprites.
 */
export type ParticleShape =
  | 'circle'
  | 'star-of-david'
  | 'menorah'
  | 'scroll'
  | 'chai'
  | 'hamsa'
  | 'dove'
  | 'pomegranate';

/**
 * Configuration for particle system.
 */
export interface ParticleConfig {
  enabled: boolean;
  count: number; // 0-200 (doubled from 100)
  minSize: number; // Minimum particle size in pixels
  maxSize: number; // Maximum particle size in pixels (up to 60)
  opacity: number; // 0-1
  speed: number; // Movement speed (0-1)
  interactive: boolean; // Respond to scroll/touch
  shapes: ParticleShape[]; // Multiple shapes can be selected
  color: string; // Particle color
  // Parallax/Depth settings
  depthEnabled: boolean; // Enable parallax depth layers
  depthLayers: number; // Number of depth layers (1-5)
  depthSpeedFactor: number; // How much depth affects speed (0-1)
  depthScaleFactor: number; // How much depth affects scale (0-1)
  depthOpacityFactor: number; // How much depth affects opacity (0-1)
  // Sparkle settings
  sparkleEnabled: boolean;
  sparkleInterval: number; // Seconds between sparkles (1-20)
  sparkleDuration: number; // How long sparkle lasts (0.5-5)
  // Drift settings
  driftEnabled: boolean; // Organic wandering motion
  driftStrength: number; // How much particles wander (0-1)
  // Glow settings
  glowEnabled: boolean;
  glowIntensity: number; // 0-1
  // Cinematic effects
  lensBokeEnabled: boolean; // Hexagonal bokeh particles
  lensBokeCount: number; // Number of bokeh particles (0-30)
  lensBokeSize: number; // Size of bokeh (10-100)
}

export const PARTICLE_SHAPE_OPTIONS: { value: ParticleShape; label: string }[] = [
  { value: 'circle', label: 'Circles' },
  { value: 'star-of-david', label: 'Star of David ✡' },
  { value: 'menorah', label: 'Menorah 🕎' },
  { value: 'scroll', label: 'Torah Scroll 📜' },
  { value: 'chai', label: 'Chai חי' },
  { value: 'hamsa', label: 'Hamsa 🤚' },
  { value: 'dove', label: 'Dove 🕊' },
  { value: 'pomegranate', label: 'Pomegranate' },
];

// ============================================
// BLUR CONFIGURATION
// ============================================

/**
 * What layers to apply blur to.
 */
export type BlurTarget = 'all' | 'gradients' | 'none';

/**
 * Configuration for blur layer.
 */
export interface BlurConfig {
  enabled: boolean;
  amount: number; // Blur amount in pixels (0-20)
  applyTo: BlurTarget;
}

export const BLUR_TARGET_OPTIONS: { value: BlurTarget; label: string }[] = [
  { value: 'all', label: 'All Layers' },
  { value: 'gradients', label: 'Gradients Only' },
  { value: 'none', label: 'None' },
];

// ============================================
// STATE OVERLAY CONFIGURATION
// ============================================

/**
 * Emotional state types for contextual overlays.
 */
export type StateType =
  | 'celebration'
  | 'gratitude'
  | 'contemplation'
  | 'connection'
  | 'reflection'
  | 'none';

/**
 * State overlay configuration.
 */
export interface StateOverlayConfig {
  type: StateType;
  colors: [string, string]; // Gradient colors for the state
  particleBurst: boolean; // Trigger particle burst on activation
  duration: number; // How long the state lasts in ms
}

export const STATE_TYPE_OPTIONS: { value: StateType; label: string }[] = [
  { value: 'none', label: 'None (Default)' },
  { value: 'celebration', label: 'Celebration (Joyful)' },
  { value: 'gratitude', label: 'Gratitude (Warm)' },
  { value: 'contemplation', label: 'Contemplation (Calm)' },
  { value: 'connection', label: 'Connection (Community)' },
  { value: 'reflection', label: 'Reflection (Introspective)' },
];

/**
 * Colors associated with each state.
 */
export const STATE_COLORS: Record<StateType, [string, string]> = {
  celebration: ['rgba(255, 215, 0, 0.15)', 'rgba(255, 165, 0, 0.1)'], // Gold
  gratitude: ['rgba(255, 182, 193, 0.15)', 'rgba(255, 218, 185, 0.1)'], // Warm pink
  contemplation: ['rgba(147, 112, 219, 0.12)', 'rgba(138, 43, 226, 0.08)'], // Purple
  connection: ['rgba(135, 206, 250, 0.12)', 'rgba(176, 224, 230, 0.08)'], // Light blue
  reflection: ['rgba(216, 191, 216, 0.12)', 'rgba(221, 160, 221, 0.08)'], // Soft lavender
  none: ['transparent', 'transparent'],
};

// ============================================
// SEASONAL THEME CONFIGURATION
// ============================================

/**
 * Seasonal theme options.
 */
export type Season = 'spring' | 'summer' | 'autumn' | 'winter' | 'shabbat' | 'auto';

/**
 * Seasonal theme configuration.
 */
export interface SeasonalConfig {
  season: Season;
  colorShift: number; // Hue rotation in degrees (-180 to 180)
  particleColor: string;
}

export const SEASON_OPTIONS: { value: Season; label: string }[] = [
  { value: 'auto', label: 'Auto (Jewish Calendar)' },
  { value: 'spring', label: 'Spring (Pesach)' },
  { value: 'summer', label: 'Summer (Shavuot)' },
  { value: 'autumn', label: 'Autumn (High Holidays)' },
  { value: 'winter', label: 'Winter (Chanukah)' },
  { value: 'shabbat', label: 'Shabbat' },
];

/**
 * Season color configurations.
 */
export const SEASON_COLORS: Record<
  Exclude<Season, 'auto'>,
  { primary: string; particle: string; shift: number }
> = {
  spring: { primary: '#90EE90', particle: '#98FB98', shift: 60 }, // Light green
  summer: { primary: '#87CEEB', particle: '#ADD8E6', shift: 180 }, // Sky blue
  autumn: { primary: '#DAA520', particle: '#D2691E', shift: 30 }, // Golden
  winter: { primary: '#E0FFFF', particle: '#B0E0E6', shift: 200 }, // Icy blue
  shabbat: { primary: '#DDA0DD', particle: '#BA55D3', shift: 280 }, // Purple/violet
};

// ============================================
// PAGE EFFECTS CONFIGURATION
// ============================================

/**
 * Complete effects configuration for a single page.
 */
export interface PageEffectsConfig {
  pageId: PageType;
  gradient: GradientConfig;
  particles: ParticleConfig;
  blur: BlurConfig;
  seasonal: SeasonalConfig;
  defaultState: StateType;
}

// ============================================
// GLOBAL CONFIGURATION
// ============================================

/**
 * Animation level options.
 */
export type AnimationLevel = 'static' | 'gentle' | 'dynamic';

/**
 * Performance mode options.
 */
export type PerformanceMode = 'auto' | 'high' | 'low';

/**
 * Device capability levels.
 */
export type DeviceCapability = 'high' | 'medium' | 'low';

export const ANIMATION_LEVEL_OPTIONS: {
  value: AnimationLevel;
  label: string;
  description: string;
}[] = [
  { value: 'static', label: 'Static', description: 'No animation, best performance' },
  { value: 'gentle', label: 'Gentle', description: 'Slow, subtle animations' },
  { value: 'dynamic', label: 'Dynamic', description: 'Responsive, interactive effects' },
];

export const PERFORMANCE_MODE_OPTIONS: {
  value: PerformanceMode;
  label: string;
  description: string;
}[] = [
  { value: 'auto', label: 'Auto', description: 'Detect device capabilities' },
  { value: 'high', label: 'High Quality', description: 'Full effects, may impact battery' },
  { value: 'low', label: 'Power Saver', description: 'Reduced effects, better battery' },
];

/**
 * Main visual effects configuration stored in Firestore.
 * Path: artifacts/{appId}/config/visualEffects
 */
export interface VisualEffectsConfig {
  /** Master toggle for entire effects system */
  enabled: boolean;
  /** Global animation behavior */
  animationLevel: AnimationLevel;
  /** Performance optimization mode */
  performanceMode: PerformanceMode;
  /** Per-page configurations */
  pages: Record<PageType, PageEffectsConfig>;
  /** Global seasonal override (applies when page seasonal is 'auto') */
  globalSeasonal: SeasonalConfig;
  /** Last update timestamp */
  updatedAt?: Timestamp;
  /** User ID who last updated */
  updatedBy?: string;
}

// ============================================
// DEFAULT CONFIGURATIONS
// ============================================

/**
 * Default gradient config.
 */
export const DEFAULT_GRADIENT: GradientConfig = {
  enabled: true,
  type: 'radial',
  position: 'top-center',
  colors: ['rgba(142, 69, 133, 0.15)', 'rgba(142, 69, 133, 0.02)'],
  opacity: 0.8,
  size: 1.2,
};

/**
 * Default particle config.
 */
export const DEFAULT_PARTICLES: ParticleConfig = {
  enabled: true,
  count: 30,
  minSize: 6,
  maxSize: 18,
  opacity: 0.5,
  speed: 0.15,
  interactive: false,
  shapes: ['star-of-david'],
  color: 'rgba(142, 69, 133, 0.4)',
  // Parallax settings
  depthEnabled: true,
  depthLayers: 3,
  depthSpeedFactor: 0.5,
  depthScaleFactor: 0.3,
  depthOpacityFactor: 0.3,
  // Sparkle settings
  sparkleEnabled: true,
  sparkleInterval: 8,
  sparkleDuration: 2,
  // Drift settings
  driftEnabled: true,
  driftStrength: 0.3,
  // Glow settings
  glowEnabled: true,
  glowIntensity: 0.4,
  // Lens bokeh
  lensBokeEnabled: false,
  lensBokeCount: 10,
  lensBokeSize: 40,
};

/**
 * Default blur config.
 */
export const DEFAULT_BLUR: BlurConfig = {
  enabled: false,
  amount: 0,
  applyTo: 'none',
};

/**
 * Default seasonal config.
 */
export const DEFAULT_SEASONAL: SeasonalConfig = {
  season: 'auto',
  colorShift: 0,
  particleColor: 'rgba(142, 69, 133, 0.3)',
};

/**
 * Create default page config.
 */
export function createDefaultPageConfig(pageId: PageType): PageEffectsConfig {
  const pageDefaults: Record<PageType, Partial<PageEffectsConfig>> = {
    study: {
      gradient: { ...DEFAULT_GRADIENT, position: 'top-center' },
      particles: {
        ...DEFAULT_PARTICLES,
        count: 25,
        speed: 0.12,
        shapes: ['star-of-david', 'scroll'],
      },
      defaultState: 'contemplation',
    },
    shmooze: {
      gradient: { ...DEFAULT_GRADIENT, position: 'center' },
      particles: { ...DEFAULT_PARTICLES, count: 35, speed: 0.2, shapes: ['circle', 'dove'] },
      defaultState: 'connection',
    },
    library: {
      gradient: { ...DEFAULT_GRADIENT, position: 'corner-right' },
      particles: {
        ...DEFAULT_PARTICLES,
        count: 20,
        speed: 0.08,
        shapes: ['scroll', 'star-of-david'],
      },
      defaultState: 'contemplation',
    },
    prayers: {
      gradient: { ...DEFAULT_GRADIENT, position: 'top-center' },
      particles: {
        ...DEFAULT_PARTICLES,
        count: 30,
        speed: 0.15,
        shapes: ['menorah', 'star-of-david'],
      },
      defaultState: 'contemplation',
    },
    journey: {
      gradient: { ...DEFAULT_GRADIENT, position: 'center' },
      particles: { ...DEFAULT_PARTICLES, count: 25, speed: 0.18, shapes: ['chai', 'hamsa'] },
      defaultState: 'reflection',
    },
  };

  const defaults = pageDefaults[pageId];

  return {
    pageId,
    gradient: defaults.gradient || DEFAULT_GRADIENT,
    particles: defaults.particles || DEFAULT_PARTICLES,
    blur: DEFAULT_BLUR,
    seasonal: DEFAULT_SEASONAL,
    defaultState: defaults.defaultState || 'none',
  };
}

/**
 * Default complete visual effects config.
 */
export const DEFAULT_VISUAL_EFFECTS_CONFIG: Omit<VisualEffectsConfig, 'updatedAt' | 'updatedBy'> = {
  enabled: true,
  animationLevel: 'gentle',
  performanceMode: 'auto',
  pages: {
    study: createDefaultPageConfig('study'),
    shmooze: createDefaultPageConfig('shmooze'),
    library: createDefaultPageConfig('library'),
    prayers: createDefaultPageConfig('prayers'),
    journey: createDefaultPageConfig('journey'),
  },
  globalSeasonal: DEFAULT_SEASONAL,
};

// ============================================
// CONTEXT VALUE TYPE
// ============================================

/**
 * Value provided by VisualEffectsContext.
 */
export interface VisualEffectsContextValue {
  /** Full configuration */
  config: VisualEffectsConfig;
  /** Whether config is loading */
  isLoading: boolean;
  /** Detected device capability */
  deviceCapability: DeviceCapability;
  /** Effective performance level (after auto-detection) */
  effectivePerformance: DeviceCapability;
  /** Current season (auto-detected or overridden) */
  currentSeason: Exclude<Season, 'auto'>;
  /** Whether it's currently Shabbat */
  isShabbat: boolean;
  /** Current temporary state overlay */
  temporaryState: StateType | null;
  /** Trigger a temporary state overlay */
  triggerState: (state: StateType, durationMs?: number) => void;
  /** Clear temporary state */
  clearState: () => void;
  /** Get config for a specific page */
  getPageConfig: (page: PageType) => PageEffectsConfig;
  /** Check if effects are enabled for a page */
  isPageEffectsEnabled: (page: PageType) => boolean;
}

// ============================================
// PARTICLE TYPES (for engine)
// ============================================

/**
 * Individual particle state with enhanced properties.
 */
export interface Particle {
  x: number;
  y: number;
  size: number;
  opacity: number;
  velocityX: number;
  velocityY: number;
  life: number;
  maxLife: number;
  // Enhanced properties
  depth: number; // 0 = far, 1 = near (for parallax)
  sparklePhase: number; // Current phase in sparkle cycle (0-1)
  driftOffset: number; // Unique offset for organic motion
  rotation: number; // Current rotation angle
  rotationSpeed: number; // Rotation speed
  shape: ParticleShape; // Individual particle shape
  isBokeh?: boolean; // Is this a lens bokeh particle
}
